import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { ITask2ReportWidget, TTask2ReportSummaries } from '../fi-task-v2-report';
import { FiTaskReportType } from '../fi-task-v2-report-enums';
import { FiTaskV2ReportDataService } from '../services/fi-task-v2-report-data.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ObjectTypes } from '@fi-shared/types';
import { FiCommonSharedService } from '@fi-shared/fi-shared-services/fi-common-shared.service';
import { FiTaskV2ReportHelperService } from '../services/fi-task-v2-report-helper.service';
import { FiTaskV2ReportService } from '../services/fi-task-v2-report.service';
import { FiItemsListFilterHelperService } from '@fi-app/shared/fi-items-list/fi-items-list-filter/fi-items-list-filter-helper.service';
import { FiTask2ReportDateService } from '../services/fi-task-v2-report-date.service';
import { isNullOrUndefined } from '@fi-shared/utils/is-null-or-undefined';
import { ItemsListFilterDialogSnackbarService } from '@fi-shared/fi-items-list/fi-items-list-filter/services/items-list-filter-dialog-snackbar.service';

@Component({
    selector: 'fi-task-v2-report',
    templateUrl: './fi-task-v2-report.component.html',
    styleUrl: './fi-task-v2-report.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-task-v2-report' },
    standalone: false,
})
export class FiTaskV2ReportComponent implements OnInit, OnDestroy {
    private ngOnDestroy$ = new Subject<void>();
    protected readonly FiTaskReportType = FiTaskReportType;
    private summaries: TTask2ReportSummaries;

    constructor(
        private fiTaskV2ReportDataService: FiTaskV2ReportDataService,
        private commonService: FiCommonSharedService,
        private fiTaskV2ReportHelperService: FiTaskV2ReportHelperService,
        private fiTaskV2ReportService: FiTaskV2ReportService,
        private fiItemsListFilterHelperService: FiItemsListFilterHelperService,
        private cdr: ChangeDetectorRef,
        private fiTask2ReportDateService: FiTask2ReportDateService,
        private itemsListFilterDialogSnackbarService: ItemsListFilterDialogSnackbarService,
    ) {}

    private _loading$ = new BehaviorSubject<boolean>(false);

    protected get loading$() {
        return this._loading$.asObservable();
    }

    private _widgets$ = new BehaviorSubject<ITask2ReportWidget[]>([]);

    protected get widgets$() {
        return this._widgets$.asObservable();
    }

    public ngOnInit(): void {
        this.loadReportContainer().then(() => {
            // https://chainformation.atlassian.net/browse/FI-6520?focusedCommentId=67987&sourceType=mention
            this.loadSummary();
        });
        this.subscribeOnAddNewWidget();
        this.subscribeOnSaveReportWidget();
        this.fiTaskV2ReportHelperService.setCurrentTaskItem(undefined);
    }

    private loadSummary(): void {
        this._loading$.next(true);
        this.fiTaskV2ReportService
            .getReportSummaries(this._widgets$.value)
            .then((summaries) => {
                this.summaries = summaries;
            })
            .finally(() => this._loading$.next(false));
    }

    protected getSummary(widget: ITask2ReportWidget) {
        return this.summaries[widget.Id];
    }

    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    protected onReportDrop(event: CdkDragDrop<ITask2ReportWidget[]>): void {
        let widgets = this._widgets$.getValue();
        moveItemInArray(widgets, event.previousIndex, event.currentIndex);
        this.commonService.updateSortIndexes(widgets, ObjectTypes.Task2Reports);
        widgets = widgets.map((widget: ITask2ReportWidget, index: number) => {
            widget.SortIndex = index;
            return widget;
        });
        this._widgets$.next(widgets);
    }

    protected editWidget(widget: ITask2ReportWidget) {
        this.fiTaskV2ReportService.saveReportWidget(widget).then((id: number) => {
            // ? show confirmation snackbar?
            this.setReportContainer();
        });
    }
    protected deleteWidget(widget: ITask2ReportWidget) {
        this.fiTaskV2ReportService.deleteReportWidget(widget.Id).then(() => {
            // ? show confirmation snackbar?
            this.setReportContainer();
        });
    }

    private loadReportContainer(): Promise<void> {
        this._loading$.next(true);
        return this.fiTaskV2ReportDataService
            .getReportContainer()
            .then((reportContainer) => {
                this._widgets$.next(reportContainer.ReportWidgets);
            })
            .finally(() => this._loading$.next(false));
    }

    private setReportContainer(widget?: ITask2ReportWidget): void {
        this.fiTaskV2ReportService.getReportContainer().then((reportContainer) => {
            this.fiTaskV2ReportDataService.setReportContainer(reportContainer);
            this.loadReportContainer().then(() => {
                if (!isNullOrUndefined(widget)) {
                    this.getReportSummary(widget);
                }
            });
        });
    }

    private getReportSummary(widget: ITask2ReportWidget): void {
        this.fiTaskV2ReportService
            .getReportSummary({
                ReportType: widget.ReportType,
                Filter: widget.Settings.Filters[0],
                DateRange: this.fiTask2ReportDateService.getDateRange(widget.Id),
                LoadSummary: true,
                LoadAvailableItems: false,
            })
            .then((summary) => {
                this.summaries[widget.Id] = summary;
                this.cdr.markForCheck();
            });
    }

    private subscribeOnAddNewWidget() {
        this.fiTaskV2ReportHelperService.openAddReportWidgetDialog$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            this.fiTaskV2ReportHelperService.openAddReportWidgetDialog();
        });
    }

    private subscribeOnSaveReportWidget(): void {
        this.fiTaskV2ReportHelperService.saveReportWidget$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((widget) => {
            this.addNewWidget(widget);
        });
    }

    private addNewWidget(widget: ITask2ReportWidget) {
        this.fiTaskV2ReportService.saveReportWidget(widget).then((id: number) => {
            widget.Id = id;
            this.itemsListFilterDialogSnackbarService.showWidgetAddedSnackbar();
            this.setReportContainer(widget);
        });
    }
}
